<template>
  <hr-adaptive-form-dialog
    ref="dialogRef"
    size="sm"
    :title="title"
    @close="hide"
    @hide="onDialogHide"
  >
    <div class="q-gutter-y-md">
      <div>
        {{ text }}
      </div>
      <client-selector
        return-value
        @input="onSelectClient"
      />
      <div
        v-if="sessionSelector"
      >
        <hr-list-select
          v-model="selectedSession"
          :label="sessionLabel"
          :options="sessionList"
          :loading="isLoadingSessions"
          :rules="[val => !!val]"
        />
      </div>
    </div>
    <template #actions="{ isMobile }">
      <hr-dismiss-btn
        v-if="!isMobile"
        :label="$t('common.buttonCancel')"
        @click="onCancelClick"
      />
      <hr-confirm-btn
        type="submit"
        :class="{ 'full-width': isMobile }"
        :label="okButton"
        :disable="noSelectedClient"
        @click="onOKClick"
      />
    </template>
  </hr-adaptive-form-dialog>
</template>
<script>
import ClientSelector from 'components/ClientSelector';
import { chain, isEmpty, map } from 'lodash';
import { mapState } from 'pinia';
import { HrAdaptiveFormDialog, HrConfirmBtn, HrDismissBtn, HrListSelect } from 'src/design-system';
import { useTime } from 'src/composables/time';
import SessionsDB from 'src/fb/sessions-db';
import { auth } from 'src/store/auth';

export default {
  name: 'SelectClient',

  components: {
    ClientSelector,
    HrAdaptiveFormDialog,
    HrConfirmBtn,
    HrDismissBtn,
    HrListSelect,
  },

  props: {
    title: {
      type: String,
      required: true,
    },
    text: {
      type: String,
      required: true,
    },
    okButton: {
      type: String,
      required: true,
    },
    sessionSelector: {
      type: Boolean,
      default: false,
    },
    sessionLabel: {
      type: String,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { getLocaleString } = useTime();
    return { getLocaleString };
  },

  data() {
    return {
      isLoadingSessions: false,
      selectedClient: null,
      selectedSession: null,

      sessionList: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient']),

    noSelectedClient() {
      return isEmpty(this.selectedClient);
    },
  },

  async created() {
    this.selectedClient = this.currentClient;

    if (!isEmpty(this.selectedClient)) {
      await this.loadSessions();
      this.selectedSession = chain(this.sessionList)
        .head()
        .get('value')
        .value();
    }
  }, // created()

  methods: {

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialogRef.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialogRef) {
        this.$refs.dialogRef.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async onOKClick() {
      this.$emit('ok', { client: this.selectedClient, session: this.selectedSession });
      this.hide();
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    async onSelectClient(client) {
      this.selectedClient = client;
      this.selectedSession = null;
      await this.loadSessions();
      this.selectedSession = chain(this.sessionList)
        .head()
        .get('value')
        .value();
    },

    async loadSessions() {
      if (!this.sessionSelector) {
        return;
      }
      this.isLoadingSessions = true;
      const sessionsDb = new SessionsDB();
      const sessions = await sessionsDb.readAll(
        [
          ['client', '==', this.selectedClient.id],
          ['status', '==', 'done'],
        ],
        'runTimestamp',
        'desc',
        10,
      );
      this.sessionList = map(sessions, (session) => ({
        value: session.id,
        label: `${
          session.sessionName
          || this.$t('sessions.sessionCard.adHocSessionTitle')
        }`,
        caption: this.getLocaleString(session.runTimestamp),
      }));
      this.isLoadingSessions = false;
    },

  },

};
</script>
